import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { AdminPlatformApps, PlatformAppSummary } from '@traceio-org/platform-contracts';
import { EmptyPageState, NavigationGlyph, PageHeading, PageSection, RecordStatus } from '@traceio-org/ui';
import { AdminPage } from '../components/AdminPage';
import { AdminProblem } from '../components/AdminProblem';
import { LoadingBlock, useNow } from '../components/feedback';
import { ScrollTable } from '../components/records';
import { useAbilities, useAdmin } from '../context';
import { formatDateTime, plural } from '../format';
import { APP_STATUS, ENVIRONMENT_NAMES, describeFailure, formatAge, sortApps } from '../health';
import { APP_STATE, controlBlocked, deploymentWords } from '../platformApps';
import { useAdminRead } from '../useAdminRead';

/** Ready instances of the services and workers, in words ("5 components, all ready"). */
export function componentsWords(app: Pick<PlatformAppSummary, 'components'>): string {
  const sized = app.components.filter((c) => c.replicasDesired !== null);
  const short = sized.filter((c) => (c.replicasReady ?? 0) < (c.replicasDesired ?? 0));
  const count = plural(app.components.length, 'component', 'components');
  if (app.components.length === 0) return 'No component reported';
  if (sized.length === 0) return count;
  return short.length === 0 ? `${count}, all ready` : `${count}, ${short.length} not ready`;
}

function AppsTable({ apps, now }: { apps: PlatformAppSummary[]; now: number }) {
  return (
    <ScrollTable caption="App Platform apps" className="admin-table admin-table--apps">
      <thead>
        <tr>
          <th scope="col">App</th>
          <th scope="col">State</th>
          <th scope="col">Health</th>
          <th scope="col">Components</th>
          <th scope="col">Live version</th>
        </tr>
      </thead>
      <tbody>
        {apps.map((app) => {
          const state = APP_STATE[app.state];
          const status = APP_STATUS[app.status];
          const secondary = [app.displayName === app.name ? null : app.name, app.host]
            .filter(Boolean)
            .join(' · ');
          return (
            <tr key={app.id}>
              <th scope="row">
                <Link to={`/apps/${app.id}`}>{app.displayName}</Link>
                {secondary ? <span className="admin-cell-secondary">{secondary}</span> : null}
              </th>
              <td>
                <RecordStatus tone={state.tone}>{state.label}</RecordStatus>
                {app.inProgress ? (
                  <span className="admin-cell-secondary">{deploymentWords(app.inProgress)}</span>
                ) : null}
              </td>
              <td>
                <RecordStatus tone={status.tone}>{status.label}</RecordStatus>
                {app.reasons[0] ? <span className="admin-cell-secondary">{app.reasons[0]}</span> : null}
              </td>
              <td>{componentsWords(app)}</td>
              <td>
                {app.deployment ? (
                  <>
                    {app.deployment.commit ? <code>{app.deployment.commit.slice(0, 7)}</code> : null}{' '}
                    <time
                      dateTime={app.deployment.createdAt}
                      title={formatDateTime(app.deployment.createdAt)}
                    >
                      {formatAge(app.deployment.createdAt, now)}
                    </time>
                  </>
                ) : (
                  <span className="admin-muted">Nothing deployed</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </ScrollTable>
  );
}

/** The one line that says the controls are off here and why (never a row of dead buttons without a reason). */
export function ControlsNote({ apps }: { apps: Pick<AdminPlatformApps, 'controls'> }) {
  const canOperate = useAbilities().operateApps;
  const blocked = controlBlocked('restart', {
    controls: apps.controls,
    canOperate,
    state: 'running',
    deploying: false,
  });
  if (!blocked) return null;
  return (
    <div className="admin-dev-note" role="note">
      <p>{blocked} You can still look at every app here.</p>
    </div>
  );
}

/**
 * Apps (/apps): this Trace Admin's own environment's App Platform apps, each linking to its page with its components,
 * deployments and controls. Every staff role reads it; the list comes from Health's cached DigitalOcean reads.
 */
export function AppsPage() {
  const { api } = useAdmin();
  const now = useNow(1000);
  // The opening read is audited; Check again reads the open page again (refresh), which is not.
  const [reads, setReads] = useState(0);
  const read = useAdminRead(
    `apps\u0000${reads}`,
    (signal) => api.listPlatformApps(reads > 0, signal),
    'apps',
  );
  const apps = read.failure ? null : read.data;

  let body: ReactNode;
  if (read.failure) {
    body = <AdminProblem failure={read.failure} subject="apps" onRetry={read.retry} />;
  } else if (!apps) {
    body = <LoadingBlock label="Reading DigitalOcean" />;
  } else if (!apps.connected) {
    body = (
      <EmptyPageState
        icon={<NavigationGlyph name="Container" />}
        title="Apps isn't connected to DigitalOcean in this environment"
        description={
          apps.notConnectedReason === 'no_project'
            ? `No DigitalOcean project is configured for ${ENVIRONMENT_NAMES[apps.environment]} here, so there is no app to show.`
            : 'platform-api has no read-only DigitalOcean token in this environment, so there is no app to show.'
        }
      />
    );
  } else {
    // Health's order: the apps that need someone first.
    const sorted = sortApps(apps.apps);
    body = (
      <div className="admin-stack">
        {apps.source === 'fake' ? (
          <div className="admin-dev-note" role="note">
            <p>
              Synthetic data: this environment reads Trace&apos;s local stand-in for DigitalOcean, not
              DigitalOcean itself. Its controls change only the stand-in.
            </p>
          </div>
        ) : null}
        <ControlsNote apps={apps} />
        {apps.failures.length > 0 ? (
          <div className="admin-dev-note" role="note">
            <p>Some of DigitalOcean&apos;s answers are missing:</p>
            <ul>
              {apps.failures.map((failure, index) => (
                <li key={index}>{describeFailure(failure)}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <PageSection
          title="Apps"
          description="Open an app for its components, its deployments and the controls. Logs, settings and scaling stay in DigitalOcean, linked from each app."
        >
          {sorted.length > 0 ? (
            <AppsTable apps={sorted} now={now} />
          ) : (
            <p className="admin-muted">DigitalOcean lists no app in this project.</p>
          )}
        </PageSection>
      </div>
    );
  }

  return (
    <AdminPage title="Apps">
      <PageHeading
        title="Apps"
        actions={
          <div className="admin-health-actions">
            {apps ? (
              <p className="admin-health-environment">
                <span className="admin-sr-only">Environment: </span>
                {ENVIRONMENT_NAMES[apps.environment]}
              </p>
            ) : null}
            {apps?.connected ? (
              <p className="admin-health-checked" data-state={apps.failures.length > 0 ? 'partial' : 'ok'}>
                <span className="admin-health-checked__dot" aria-hidden="true" />
                <span>
                  Checked{' '}
                  <time dateTime={apps.checkedAt} title={formatDateTime(apps.checkedAt)}>
                    {formatAge(apps.checkedAt, now)}
                  </time>
                </span>
              </p>
            ) : null}
            <button
              type="button"
              className="admin-action"
              onClick={() => setReads((n) => n + 1)}
              aria-disabled={read.loading || undefined}
            >
              Check again
            </button>
          </div>
        }
      />
      {body}
    </AdminPage>
  );
}
